import { Box, Button, Chip, IconButton, MenuItem, Paper, Select, Stack, TextField, Typography } from '@mui/material';
import { AddIcon, RemoveItemIcon } from '../../../components/icons';
import { RecurringInvoiceFragmentFragment } from '../../../generated/graphql';
import { useEffect, useState } from 'react';
import { useApp } from '../../../components/app-context';
import { useAlert } from '@dwelvio/react-alert';

interface RecurringInvoiceIncomeAssignmentPanelProps {
  recurringInvoice: RecurringInvoiceFragmentFragment; // Replace with actual type
  loading: boolean;
  onSaveAssignments: (unitAssignments: UnitAssignmentRow[]) => Promise<void>;
}

export interface UnitAssignmentRow {
  unitId: string;
  amount: string;
}

export function RecurringInvoiceIncomeAssignmentPanel(props: RecurringInvoiceIncomeAssignmentPanelProps) {
  const { recurringInvoice, loading, onSaveAssignments } = props;

  // Unit assignments editing
  const [assignmentRows, setAssignmentRows] = useState<UnitAssignmentRow[]>([]);
  const [assignmentsEdited, setAssignmentsEdited] = useState(false);
  const app = useApp();
  const { show } = useAlert();

  // Unit assignment helpers - only allow units from the contract
  const contractUnits = recurringInvoice?.contract?.units ?? [];
  const assignmentTotal = assignmentRows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
  const recurringInvoiceAmount = recurringInvoice?.totalAmount ?? 0;
  const assignmentTotalMatchesInvoice =
    assignmentRows.length === 0 || Math.abs(assignmentTotal - recurringInvoiceAmount) < 0.001;
  const isDeleted = recurringInvoice.deletedAt !== null;

  const handleAddAssignmentRow = () => {
    setAssignmentRows((prev) => [...prev, { unitId: '', amount: '' }]);
    setAssignmentsEdited(true);
  };

  const handleRemoveAssignmentRow = (idx: number) => {
    setAssignmentRows((prev) => prev.filter((_, i) => i !== idx));
    setAssignmentsEdited(true);
  };

  const handleAssignmentChange = (idx: number, field: 'unitId' | 'amount', value: string) => {
    setAssignmentRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
    setAssignmentsEdited(true);
  };

  const handleSaveAssignments = async () => {
    if (!recurringInvoice) return;
    if (assignmentRows.length > 0 && !assignmentTotalMatchesInvoice) {
      show.error(
        `Súčet priradených súm (${app.formatNumber(assignmentTotal, 2)} €) sa musí rovnať celkovej sume faktúry (${app.formatNumber(recurringInvoiceAmount, 2)} €).`,
      );
      return;
    }
    const validRows = assignmentRows.filter((r) => r.unitId && parseFloat(r.amount) >= 0);
    await onSaveAssignments(validRows);
    setAssignmentsEdited(false);
  };

  useEffect(() => {
    if (recurringInvoice) {
      // Initialise assignment rows from fetched data, or pre-fill from contract units
      if (recurringInvoice.unitAssignments?.length) {
        setAssignmentRows(
          recurringInvoice.unitAssignments.map((a) => ({
            unitId: a.unitId,
            amount: a.amount.toString(),
          })),
        );
      } else if (recurringInvoice.contract?.units?.length) {
        setAssignmentRows(
          recurringInvoice.contract.units.map((u) => ({
            unitId: u.id,
            amount: '0',
          })),
        );
      } else {
        setAssignmentRows([]);
      }
      setAssignmentsEdited(false);
    }
  }, [recurringInvoice]);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6">Priradenie príjmov</Typography>
        {!loading && !isDeleted && contractUnits.length > 0 && (
          <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleAddAssignmentRow}>
            Pridať
          </Button>
        )}
      </Stack>

      {assignmentRows.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Nemá žiadne priradenia príjmov.
        </Typography>
      )}

      <Stack spacing={1}>
        {assignmentRows.map((row, idx) => (
          <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Select
                value={row.unitId}
                onChange={(e) => handleAssignmentChange(idx, 'unitId', e.target.value)}
                displayEmpty
                size="small"
                disabled={loading || isDeleted}
                sx={{ flex: 1, minWidth: 120 }}
              >
                <MenuItem value="">— vybrať —</MenuItem>
                {contractUnits.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name}
                  </MenuItem>
                ))}
              </Select>
              <TextField
                value={row.amount}
                onChange={(e) => handleAssignmentChange(idx, 'amount', e.target.value)}
                type="number"
                size="small"
                disabled={loading || isDeleted}
                label="Suma (€)"
                inputProps={{ step: '0.01', min: '0' }}
                sx={{ width: 120 }}
              />
              {!loading && !isDeleted && (
                <IconButton size="small" onClick={() => handleRemoveAssignmentRow(idx)}>
                  <RemoveItemIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
          </Paper>
        ))}
      </Stack>

      {assignmentRows.length > 0 && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
          <Typography variant="body2" color={assignmentTotalMatchesInvoice ? 'success.main' : 'error.main'}>
            Súčet: {app.formatNumber(assignmentTotal, 2)} € / {app.formatNumber(recurringInvoiceAmount, 2)} €
          </Typography>
          {assignmentTotalMatchesInvoice && <Chip label="OK" color="success" size="small" />}
        </Stack>
      )}

      {assignmentsEdited && !isDeleted && !loading && (
        <Button variant="contained" onClick={handleSaveAssignments} disabled={loading} sx={{ mt: 1 }} size="small">
          Uložiť priradenia
        </Button>
      )}
    </Box>
  );
}
